import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomDialog from 'components/Dialog/Dialog';
import { RHFTextField, RHFTextFieldOutlined } from 'components/RHF';
import { CustomButton } from 'components/Button';
import ReconciliationIcon from 'assets/images/ReconciliationWarningIcon.svg';
import { useStoreReconciliations } from 'features/store/hooks/queries/getStoreReconciliations';
import { ReconciliationFormValues, reconciliationSchema } from 'validationSchemas/audit/reconciliationSchema';
import { useCreateReconciliation, useUpdateReconciliation } from '../../hooks';
import { formatAmountForAPI } from '../../utils';

interface ReconciliationDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit?: (data: any) => void;
    suggestedAmount?: number;
    storeId: string;
    date: string;
}



const ReconciliationDialog: React.FC<ReconciliationDialogProps> = ({
    open,
    onClose,
    onSubmit,
    suggestedAmount = 0,
    storeId,
    date
}) => {
    const [error, setError] = useState<string | null>(null);

    // Fetch existing reconciliations
    const { data: reconciliationsData } = useStoreReconciliations(storeId, {
        dateFrom: date,
        dateTo: date
    });

    // Mutations
    const createMutation = useCreateReconciliation(storeId);
    const updateMutation = useUpdateReconciliation(storeId, date);

    const isLocalLoading = createMutation.isPending || updateMutation.isPending;

    // Find existing reconciliation for the date
    const existingReconciliation = reconciliationsData?.items?.find(
        (r: any) => r.reconciliation_date === date
    ) || null;

    const isUpdateMode = !!existingReconciliation;
    const formTitle = isUpdateMode ? 'Update Reconciliation' : 'Create Reconciliation';
    const buttonText = isUpdateMode ? 'Update' : 'Create';

    const methods = useForm<ReconciliationFormValues>({
        resolver: yupResolver(reconciliationSchema),
        defaultValues: {
            amount: '',
            reason: ''
        },
        mode: 'onChange'
    });

    const { handleSubmit, setValue, reset } = methods;

    // Populate form when existing reconciliation is found
    useEffect(() => {
        if (open) {
            if (existingReconciliation) {
                reset({
                    amount: existingReconciliation.amount?.toString() || '',
                    reason: existingReconciliation.reason || ''
                });
            } else {
                reset({
                    amount: '',
                    reason: ''
                });
            }
            setError(null);
        } else {
            // Reset form when dialog closes
            reset({
                amount: '',
                reason: ''
            });
            setError(null);
        }
    }, [existingReconciliation, open, reset]);

    const handleApplySuggested = useCallback(() => {
        const numericAmount = Number(suggestedAmount) || 0;
        const formattedAmount = numericAmount.toFixed(2);
        setValue('amount', formattedAmount);
    }, [suggestedAmount, setValue]);

    const handleFormSubmit = useCallback(async (data: ReconciliationFormValues) => {
        try {
            setError(null);

            const formattedAmount = formatAmountForAPI(data.amount);

            if (isNaN(parseFloat(formattedAmount))) {
                setError('Invalid amount format');
                return;
            }

            const reconciliationData = {
                reconciliation_date: date,
                amount: formattedAmount,
                reason: data.reason
            };

            let response;
            if (existingReconciliation && existingReconciliation.id) {
                // Update mode
                response = await updateMutation.mutateAsync({
                    id: existingReconciliation.id,
                    data: reconciliationData
                });
            } else {
                // Create mode
                response = await createMutation.mutateAsync(reconciliationData);
            }

            if (response.success) {
                // Call custom onSubmit if provided
                if (onSubmit) {
                    onSubmit(data);
                }

                // Reset form and close
                reset({
                    amount: '',
                    reason: ''
                }); // Clean up form state
                onClose();
            } else {
                setError(response.error || 'Failed to save reconciliation');
            }
        } catch (err: any) {
            console.error('Error saving reconciliation:', err);
            setError(err.message || 'Error saving reconciliation');
        }
    }, [date, existingReconciliation, createMutation, updateMutation, onSubmit, onClose, reset]);

    const shouldShowSuggestedAmount = (): boolean => Math.abs(suggestedAmount) > 0.01;

    return (
        <CustomDialog
            open={open}
            title={formTitle}
            handleClose={onClose}
            handleSubmit={handleSubmit(handleFormSubmit)}
            buttonTxt={isLocalLoading ? 'Saving...' : buttonText}
            cancelBtn={true}
            cancelBtnTxt="Cancel"
            btnColor="#1b3e61"
            width="540px"
            disabled={isLocalLoading || !methods.formState.isValid}
            isLoading={isLocalLoading}
            sx={{
                "& .MuiPaper-root": {
                    borderRadius: "16px",
                    maxWidth: "560px",
                    height: 'auto',
                    maxHeight: '90vh',
                    overflow: 'hidden'
                },
                "& .MuiDialogContent-root": {
                    padding: '24px',
                    overflowY: 'hidden',
                }
            }}
        >
            <FormProvider {...methods}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {shouldShowSuggestedAmount() && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                p: 1.5,
                                bgcolor: '#FFFDE7',
                                border: '1px solid #F2E44999',
                                borderRadius: '8px',
                                alignItems: 'flex-start'
                            }}
                        >
                            <Box
                                component="img"
                                src={ReconciliationIcon}
                                sx={{ width: 44, height: 44, mt: 0.5 }}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        lineHeight: '22px',
                                        color: '#444'
                                    }}
                                >
                                    Suggested reconciliation amount based on audit analysis:
                                    <Box component="span" sx={{ fontWeight: 700, ml: 0.5 }}>
                                        ${Math.abs(suggestedAmount).toFixed(2)} ({suggestedAmount < 0 ? 'Short' : 'Over'})
                                    </Box>
                                </Typography>

                                <CustomButton
                                    variant="outlined"
                                    onClick={handleApplySuggested}
                                    disabled={isLocalLoading}
                                    sx={{
                                        borderColor: '#D19E00',
                                        borderRadius: '8px',
                                        color: '#D19E00',
                                        bgcolor: '#fff',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        padding: '4px 12px',
                                        fontSize: '14px',
                                        width: 'fit-content',
                                        '&:hover': {
                                            borderColor: '#B7950B',
                                            bgcolor: '#FFFDE7'
                                        }
                                    }}
                                >
                                    Apply Suggested Amount
                                </CustomButton>
                            </Box>
                        </Box>
                    )}

                    {/* Reconciliation Amount Field */}
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontWeight: 600,
                                fontSize: '16px',
                                mb: 1,
                                color: '#1b3e61'
                            }}
                        >
                            Reconciliation Amount ($)
                        </Typography>
                        <RHFTextField
                            name="amount"
                            placeholder="Enter amount"
                            fullWidth
                            disabled={isLocalLoading}
                        />
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontStyle: 'italic',
                                fontSize: '14px',
                                color: '#999',
                                textAlign: 'right',
                                mt: 0.5
                            }}
                        >
                            Enter amount (e.g., -25.50 for short, 15.75 for over)
                        </Typography>
                    </Box>

                    {/* Notes/Reason Field */}
                    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#1b3e61'
                            }}
                        >
                            Notes/Reason
                        </Typography>
                        <RHFTextFieldOutlined
                            name="reason"
                            placeholder="Describe the reason for this reconciliation..."
                            multiline
                            minRows={2}
                            fullWidth
                            disabled={isLocalLoading}
                        />
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontStyle: 'italic',
                                fontSize: '12px',
                                color: '#999',
                                textAlign: 'right',
                                mt: 0.5
                            }}
                        >
                            {methods.watch('reason')?.length || 0}/500 characters
                        </Typography>
                    </Box>

                </Box>
            </FormProvider>
        </CustomDialog>
    );
};

export default ReconciliationDialog;
